import {
  differenceInCalendarDays,
  differenceInHours,
  differenceInMinutes,
  format,
  formatDistanceStrict,
  formatDistanceToNow,
  isValid,
  parseISO,
} from 'date-fns';

export const formatMonthDuration = (totalMonths: number): string => {
  const years = Math.floor(totalMonths / 12);
  const months = totalMonths % 12;

  if (years === 0) return `${months}mo`;
  if (months === 0) return `${years}yr${years > 1 ? 's' : ''}`;
  return `${years}yr${years > 1 ? 's' : ''} ${months}mo`;
};

export const formatRelativeDay = (value: Date | string): string | null => {
  const date = typeof value === 'string' ? new Date(value) : value;
  if (!isValid(date)) return null;

  const days = Math.floor((Date.now() - date.getTime()) / 86400000);

  if (days < 0) return 'Just now';
  if (days === 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 30) return `${days} days ago`;
  return `${Math.floor(days / 30)}mo ago`;
};

/** Past this many days a relative time ("23 days ago") gets vague. */
const RELATIVE_TIME_DAYS = 7;

/**
 * When something happened: relative for the last week ("18 hours ago",
 * "7 days ago"), then the date ("Sep 8", with the year once it differs).
 */
export const formatRelativeTime = (
  value: Date | string,
  now = new Date(),
): string => {
  // A server clock slightly ahead of the browser must not read "in 1 minute".
  const date = new Date(Math.min(new Date(value).getTime(), now.getTime()));
  if (differenceInMinutes(now, date) < 1) return 'Just now';
  if (differenceInCalendarDays(now, date) <= RELATIVE_TIME_DAYS) {
    return formatDistanceStrict(date, now, { addSuffix: true });
  }
  return format(
    date,
    date.getFullYear() === now.getFullYear() ? 'MMM d' : 'MMM d, yyyy',
  );
};

/**
 * Calculates the time since a given date and returns a human-readable string
 * @param date - The date to compare against (can be null/undefined)
 * @returns A string representation of the time elapsed
 */
export const daysSince = (date: Date | string | null | undefined): string => {
  if (!date) {
    return 'just now';
  }

  const targetDate = typeof date === 'string' ? new Date(date) : date;
  return formatDistanceToNow(targetDate, { addSuffix: true });
};

/**
 * Determines if a given date string represents a time within the last 24 hours.
 *
 * @param createdAt - An ISO date string representing the creation time.
 * @returns `true` if the date is within the last 24 hours, otherwise `false`.
 */
export const isRecent = (createdAt: string | undefined | null) => {
  if (!createdAt) {
    return false;
  }

  const createdDate = parseISO(createdAt);
  const hours = differenceInHours(new Date(), createdDate);
  return hours < 24;
};

/**
 * Formats a date label with a prefix, handling relative and absolute times correctly.
 * e.g. "Created 5 minutes ago" vs "Created on 12/10/2025"
 */
export const formatDateLabel = (
  date: Date | string | null | undefined,
  label: string,
): string => {
  const timeString = daysSince(date);
  // formatDistanceToNow always returns strings ending with "ago" for past dates
  const isRelative = timeString.includes('ago') || timeString === 'Just now';

  return isRelative ? `${label} ${timeString}` : `${label} on ${timeString}`;
};

export const parseDateParam = (value: string | null): Date | undefined => {
  if (!value) {
    return undefined;
  }

  const parsed = parseISO(value);
  return isValid(parsed) ? parsed : undefined;
};
